import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { RecipeStore } from '@/components/recipe/stores';
import { convertRecipeRuntimeToHours } from '@/lib/utils/recipeRuntime';

export type UseRecipeActivityRuntimeOptions = {
  /** When false, recipe edits do not push into activity runtime. */
  enabled: boolean;
  onRuntimeChange: (runtime: number | undefined) => void;
  runtimeChangedManuallyInitial?: boolean;
  recipeStore: RecipeStore | null;
  runtimeUnit?: string;
};

export type RecipeRuntimeFieldHandlers = {
  onInputChange: (raw: string) => void;
  onResetToRecipe: () => void;
};

/**
 * Sync activity runtime (hours) with recipe {@link RecipeTemplateDto.calcRuntimeFormula} via {@link RecipeStore}.
 * Returns a stable {@link onRuntimeCalculated} suitable for {@link useRecipeEditor} (use a ref wrapper if the editor is created first).
 */
export function useRecipeActivityRuntime({
  enabled,
  onRuntimeChange,
  runtimeChangedManuallyInitial = false,
  recipeStore,
  runtimeUnit,
}: UseRecipeActivityRuntimeOptions) {
  const enabledRef = useRef(enabled);
  const onRuntimeChangeRef = useRef(onRuntimeChange);
  const runtimeUnitRef = useRef(runtimeUnit);

  useLayoutEffect(() => {
    enabledRef.current = enabled;
    onRuntimeChangeRef.current = onRuntimeChange;
    runtimeUnitRef.current = runtimeUnit;
  }, [enabled, onRuntimeChange, runtimeUnit]);

  const [runtimeManualOverride, setRuntimeManualOverride] = useState(runtimeChangedManuallyInitial);

  const onRuntimeCalculated = useCallback((calculatedRuntime: number) => {
    if (!enabledRef.current) return;
    onRuntimeChangeRef.current(convertRecipeRuntimeToHours(calculatedRuntime, runtimeUnitRef.current));
  }, []);

  const showResetButton = runtimeManualOverride || recipeStore?.runtimeChangedManually === true;

  const onResetToRecipe = useCallback(() => {
    if (!recipeStore) return;

    recipeStore.setRuntimeChangedManually(false);
    setRuntimeManualOverride(false);

    const calculated = recipeStore.getCalculatedRuntimeFromRecipe();
    if (calculated != null) {
      onRuntimeChangeRef.current(convertRecipeRuntimeToHours(calculated, runtimeUnitRef.current));
    }
  }, [recipeStore]);

  const onInputChange = useCallback(
    (raw: string) => {
      if (raw === '') {
        onRuntimeChangeRef.current(undefined);
        recipeStore?.setRuntimeChangedManually(false);
        setRuntimeManualOverride(false);
        return;
      }
      const value = parseFloat(raw);
      onRuntimeChangeRef.current(isNaN(value) ? undefined : value);
      recipeStore?.setRuntimeChangedManually(true);
      setRuntimeManualOverride(true);
    },
    [recipeStore]
  );

  return {
    onRuntimeCalculated: enabled ? onRuntimeCalculated : undefined,
    showResetButton,
    onResetToRecipe,
    onInputChange,
    runtimeManualOverride,
  };
}

/** Stable delegate for passing a late-bound runtime callback into {@link useRecipeEditor}. */
export function useRecipeRuntimeCalculatedDelegate() {
  const handlerRef = useRef<((runtime: number) => void) | undefined>(undefined);

  const delegate = useCallback((runtime: number) => {
    handlerRef.current?.(runtime);
  }, []);

  const bind = useCallback((handler: ((runtime: number) => void) | undefined) => {
    handlerRef.current = handler;
  }, []);

  return { delegate, bind };
}
